import type { CSSProperties, MutableRefObject, ReactNode, FocusEventHandler, MouseEventHandler } from 'react';
import type { SCFontItem } from 'src/types/fonts';
import type { IProfilePictureFile } from 'src/types/user';
import type { IOrganizationFonts, IOrganizationIntegrations } from 'src/types/organization';
import type { CustomVariable, VariableType } from 'utils/variables/types';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import {
	CARD_TYPE,
	COMPONENT_STATES,
	COMPONENT_TYPE,
	DEFAULT_MEDIA_QUERY_PLATFORMS,
	ELEMENT_EDIT_MODE,
	LAYER_TYPE,
	CONNECTION_TYPES,
	STORY_TYPE,
	CONTENT_ALIGN_Y,
	Pin,
	CountType,
} from 'common/constants';
import type { TransitionHandlerContextType } from 'client/components/common/CardTransition/Context';
import type { ITraverseTreeOptionsListeners } from 'client/components/common/BuildingBlocks/utils/traverse-tree-types';
import { SELECTION_STAGE } from 'client/components/common/SelectionHint/utils';
import type { FormCardContextType } from 'client/components/common/StoryCard/Form/types';
import { SortableType } from 'common/building-blocks/sortable-box/constants';
import type { SupportedLanguage } from 'common/i18n/types';
import type { BBModel, BBSymbolLink, BBUiConfig } from './bb-model';

export enum EditorMode {
	DESIGN = 'DESIGN',
	CONTENT = 'CONTENT',
}

export enum ProgressDirection {
	ltr = 'left',
	rtl = 'right',
}

export enum BBMetaType {
	title = 'title',
	subtitle = 'subtitle',
	questionText = 'questionText',
	answerText = 'answerText',
	answerImage = 'answerImage',
	image = 'image',
	bgImage = 'bgImage',
}

// Component-level AI template configuration (stored in individual components)
export interface ComponentAITemplate {
	key: string; // User-defined unique key like "questionTitle", "answers"
	type: 'text' | 'image' | 'flag' | 'number' | 'group';
	prompt: string;
	ref?: string; // Reference to parent group key (for group elements only)
}

export interface PublishedAITemplateItem {
	type: ComponentAITemplate['type'];
	prompt: string;
}

export interface PublishedAITemplateGroup extends PublishedAITemplateItem {
	props: Record<string, PublishedAITemplateItem>;
}

/**
 * Published story AI template configuration (aggregated at StorySettingsOfCard level)
 *
 * @example
 * 	{
 * 	  "questionTitle": {
 * 	    "type": "text",
 * 	    "prompt": "What is your favorite color?"
 * 	  },
 * 	  "answers": {
 * 	    "type": "group",
 * 	    "props": {
 * 	      "answerText": {
 * 	        "type": "text",
 * 	        "prompt": "Short answer, up to 20 characters."
 * 	      },
 * 	    }
 * 	  }
 * 	}
 */
export type PublishedAITemplate = Record<string, PublishedAITemplateItem | PublishedAITemplateGroup>;

export type ComponentTypes = ValuesType<typeof COMPONENT_TYPE>;

export type BBStates = ValuesType<typeof COMPONENT_STATES>;

export type StoryMediaPlatform = ValuesType<typeof DEFAULT_MEDIA_QUERY_PLATFORMS>;
export type StoryDefaultPlatform = Exclude<StoryMediaPlatform, typeof DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT>;

export type LayerType = ValuesType<typeof LAYER_TYPE>;

export type InfographicSelection = {
	selectionId: string;
	projectId: string;
	editorUrl: string;
	publicId: string;
	publicUrl: string;
	name: string;
	publishedVersion: number;
	transparent: boolean;
};

type BBComponentProps = BBUiConfig['componentProps'];

export type BBComponentPropsInheritance = Partial<
	Record<keyof Omit<BBComponentProps, 'styles' | 'other'>, BBInheritanceValue[] | undefined>
> &
	Record<
		keyof Pick<BBComponentProps, 'styles'>,
		Partial<Record<keyof BBComponentProps['styles'], BBInheritanceValue[] | undefined>>
	> &
	Record<
		keyof Pick<BBComponentProps, 'other'>,
		Partial<Record<keyof BBComponentProps['other'], BBInheritanceValue[] | undefined>>
	>;

export type BBInheritanceValue = {
	secondValue?: any;
	value?: any;
	state?: BBStates;
	mq?: StoryMediaPlatform;
};

export type BBInheritanceParent = {
	_id: string;
	path: string;
	type: ComponentTypes;
	name: string;
	/**
	 * This instance owns activation (intrinsic control or resolved link). The editor uses the same runtime
	 * capability boundary to stop Answer Hover authoring before this element and its descendants.
	 */
	interactionOwner: boolean;
	inheritance: {
		componentProps?: BBComponentPropsInheritance;
	};
};

/**
 * @info: props passed by traverseTree to client components (building blocks), in case target is editor
 */
export type BBEditableModeProps = {
	nodeProps?: {
		'data-path': string;
		'data-pin': Pin;
		'data-lock': boolean;
		className?: string;
		style?: CSSProperties;
	};
	eventListeners?: Partial<ITraverseTreeOptionsListeners>;
	inheritance?: {
		componentProps: BBComponentPropsInheritance;
		parent: BBInheritanceParent[];
	};
};

export type BBSymbolInstance = BBModel & BBSymbolLink;

export type CardFlowEventType = ValuesType<typeof CONNECTION_TYPES>;

/**
 * @info: src/routes/admin/components/pages/Story/Flow/Connector/ConnectorSvg/utils.js -> CardEvent
 */
export type CardFlowEvent = {
	_id: string;
	name: string;
	connection?: {
		// Type of connection (e.g., NEXT, ANY).
		type: CardFlowEventType;

		// Identifier for the connection's target point (e.g., cardId, stepId).
		pointId?: string;
	};

	// Score ranges for score-based navigation events.
	score?: number[];

	// Rate for A/B navigation events.
	rate?: number;

	// Identifier of the component associated with the event (e.g., Button, Answer, etc.).
	componentId?: string;

	/**
	 * Indicates whether this is the default event among navigation card events.
	 *
	 * This property is also used as the default for non-navigation cards.
	 * In earlier versions, this field might be absent, as the default event
	 * was determined by the event name (see FLOW_DEFAULT_EVENT_NAME).
	 */
	isDefault?: boolean;

	// Redirect URL for the event.
	redirectUrl?: string;

	// Country by geolocation
	countryIso2?: string;
};

export type CardData = {
	_id: string;
	type: ValuesType<typeof CARD_TYPE>;
	name: string;
	designMode?: import('common/design-tokens').DesignModeSelection;
	settings: {
		urlNavigationParam?: string;
		redirectUrl?: string; // Redirect URL Card
		includeUrlParams?: boolean; // Redirect URL Card
	};
	elements: BBModel[];
	events: CardFlowEvent[];
};

export type CardAction = {
	_id: string;
	type: 'completions';
	templateRaw: string;
	template: string;
	systemRaw: string;
	system: string;
	model: string;
	outputs: Record<string, string>;
	outputsRaw: Record<string, string>;
};

export type BBSymbolInfo = {
	// "masterId" links to the "_id" of symbol MASTER. Also equal to 1st symbol "_id".
	masterId: string;
	// "instanceId" links to the "_id" of symbol INSTANCE
	instanceId: string;
	// "childId" links to the "_id" of related child in symbol MASTER
	childId?: string;
};

export type SelectedBBSingle = {
	_id: string;
	type: ComponentTypes;
	designMode?: import('common/design-tokens').DesignModeSelection;
	path: string;
	uiConfig: BBUiConfig;
	children?: string | null;
	selectionStage: ValuesType<typeof SELECTION_STAGE>;
	inheritance: {
		componentProps: BBComponentPropsInheritance;
		parent: BBInheritanceParent[];
	};
} & Partial<BBSymbolLink>;

export type SelectedBB = Record<string, SelectedBBSingle>;

export type EditableStateInfo = {
	state: BBStates;
	source: {
		id: string;
		path: string;
		type: ComponentTypes | '';
		layer: LayerType;
		states?: BBStates[];
	};
};

export type StorySymbol = {
	master: BBModel;
	instance: Record<string, BBSymbolInstance>;
};

export type StorySymbols = Record<string, StorySymbol>;

type BBStatesProp = Record<Exclude<BBStates, typeof COMPONENT_STATES.DEFAULT>, boolean>;

export type BBCommonProps = {
	_id: string;
	uiConfig: BBUiConfig;
	/** Effective selection for this building block only; child building blocks resolve their own selection. */
	designModeSelection?: import('common/design-tokens').DesignModeSelection;
	editableModeProps?: BBEditableModeProps;
	children: ReactNode;
	currentMediaQuery: StoryMediaPlatform;
	mediaQuery: StoryMediaQuery;
	states: BBStatesProp; // building block current states (hover, correct, etc.)
	// parent states currently provided only by Answer to his children and by Swipe
	parentStates?: BBStatesProp; // parent building block current states (hover, correct, etc.)
	setStates: (state: Partial<Record<BBStates, boolean>>) => void; // callback to set props.states
	stateAttrs: { [key: string]: string }; // props.states converted as data attributes to assign to DOM node
	eventListeners?:
		| {
				// editor mode
				onDoubleClickCapture: MouseEventHandler;
				onFocus: FocusEventHandler;
				onMouseDown: MouseEventHandler;
				onMouseDownCapture: MouseEventHandler;
				onMouseEnter: MouseEventHandler;
				onMouseLeave: MouseEventHandler;
				onMouseOver?: MouseEventHandler;
				onClick?: MouseEventHandler;
		  }
		| {
				// non-editor mode
				onMouseEnter: MouseEventHandler;
				onMouseLeave: MouseEventHandler;
				onMouseOver?: MouseEventHandler;
				onClick?: MouseEventHandler;
		  };
	isEditableMode?: boolean;
	contentAlignY?: ValuesType<typeof CONTENT_ALIGN_Y>;
	onEditableMouseEnter?: MouseEventHandler;
	onEditableMouseLeave?: MouseEventHandler;
	onEditableClick?: MouseEventHandler;
	onEditableFocus?: FocusEventHandler;
	containerRef?: MutableRefObject<HTMLElement>;
	formContext?: FormCardContextType;
	type: ComponentTypes;
	isCardTree: boolean;
	editorMode: EditorMode;
	isEmbed: boolean;
	getComputedComponentProps: (state: BBStates) => ReturnType<ComputedComponentPropsFn>;
	/**
	 * @desc used to pass props into some BB for reuse it in other building block (e.g. Img in Share)
	 */
	custom?: {
		style: CSSProperties;
		isImageEditorDisabled?: boolean;
		isToolbarHidden?: boolean;
	};
	transitionCSS: { [p: string]: boolean } | null;
	resetChildrenTransition: () => { [componentId: string]: boolean };
} & Partial<BBSymbolLink> &
	TransitionHandlerContextType;

export type BBEditMode = ValuesType<typeof ELEMENT_EDIT_MODE>;

export type CardEditorSymbolInfo = {
	editMode: BBEditMode;
	masterId?: string;
	instanceId?: string;
};

export type StorySingleMediaQueryConfig = {
	maxWidth: number | null;
	minWidth: number | null;
	uiName: string;
};

export type StoryMediaQuery = {
	defaultPlatform: StoryMediaPlatform;
	config: Omit<Record<StoryMediaPlatform, StorySingleMediaQueryConfig>, 'default'>;
};

export type StoryStep = {
	_id: string;
	cards: CardData[];
};

type Screenshot = Record<StoryMediaPlatform, { filepath: string; hosting: string }>;

export type StorySettingsCards = {
	[cardId: string]: StorySettingsOfCard;
};

export type StorySettingsOfCard = {
	// added only in the `published` version
	type?: CardData['type'];
	answers?: {
		[answerId: string]:
			| { name: string; isCorrect?: boolean } // Trivia, TrueOrFalse
			| { name: string; score?: string; index: number } // SortableTrivia
			| { name: string; score?: string } // PersonalityTest with "countType: score"
			| { name: string; correctValue?: string } // answer-input
			// PersonalityTest with "countType: character"
			| { name: string; characterPoints?: { [characterId: string]: string } };
	};
	input?: Record<string, { name?: string }>;
	score?: string;
	maxAttempts?: string; // answer-input max attempts
	showCorrect?: boolean; // whether Answer has correct/incorrect states
	showResults?: boolean; // whether to render Answer with a special blocks for live results
	countType?: CountType; // way the Navigation card should count user result
	sortableType?: SortableType; // the way `SortableBox` should perform sorting in `SortableTrivia` card
	name?: string;
	seo?: { title?: string; description?: string; keywords?: string };
	share?: { title?: string; description?: string; msg?: string; image?: string };
	answersMinMax?: [min: number, max: number];
	forceSubmitBtn?: boolean;
	[UNIFORM_PROPS.expose]?: {
		// expose (transition) duration of the Card, Content or Float blocks
		[UNIFORM_PROPS.duration]: string; // e.g. '3'
	};
	images?: { type: BBMetaType; id: /* related component._id */ string }[];
	// AI template for content generation (aggregated from components)
	aiTemplate?: Nullable<PublishedAITemplate>;
};

export type StorySettingsType = {
	brandKitId?: string | null;
	design?: import('common/design-tokens').DesignDefinition;
	designModes?: import('common/design-tokens').StoryDesignModeSettings;
	cardOrder?: string[]; // published version only
	cards?: StorySettingsCards;
	actions?: {
		[cardId: CardAction['_id']]: CardAction;
	};
	characters?: {
		[characterId: string /* characterId is "event._id" of Navigation card with "countType: character" */]: {
			name: string;
			cardId: string;
		};
	};
	seo?: { title?: string; description?: string; keywords?: string; favicon?: string; webclip?: string };
	share?: { title?: string; description?: string; image?: string };
	fonts?: IOrganizationFonts;
	// list of used system fonts in the published version to load then
	systemFonts?: Array<Pick<SCFontItem, 'url' | 'fontType'>>;
	integrations?: IOrganizationIntegrations;
	restrictions?: {
		oncePerUser?: boolean;
		isDisabledApi?: boolean;
		isHideCredit?: boolean;
		isAutoRedirect?: boolean;
		isBrowserHistoryEnabled?: boolean;
	};
	screenshot?: Record<string, Screenshot>;
	variables?: {
		source?: Array<{ url: string; variables: CustomVariable[] }>;
		custom?: { type: VariableType.gptActionOutput; actionId: string; outputKey: string; value: string }[];
	};
	// story template id. exists if story created from template
	templateId?: number;
	// story generator data. exists if story created using AI generator
	generator?: { id: string; jobId: string };
	gdpr?: {
		enabled?: boolean;
		interactionsDisabled?: boolean;
	};
	darkMode?: {
		autoDetection?: boolean;
		showToggle?: boolean;
		enableByUrl?: boolean;
		byNavCard?: boolean;
		platforms?: string[];
		elements?: string[];
	};
};

export type StoryVersionType = {
	storyId: string;
	version: string;
	settings: StorySettingsType;
	createdAt: string;
	updatedAt: string;
	changelog: null;
	url?: string; // exists only on published stories
	data: {
		mediaQuery: StoryMediaQuery;
		elements: BBModel[];
		steps: StoryStep[];
		symbols?: StorySymbols;
		appVersion: string;
		// is card editor auto save enabled. `true | undefined` = enabled; `false` = disabled
		cardEditorAutoSync?: boolean;
	};
};

export type StoryVersions = {
	[key: string]: StoryVersionType;
};

interface StorycardsDomain {
	domain: 'dev.storycards.co' | 'storycards.co' | 'stories.sc' | 'story.dev.story-cards.com';
	images: 'images-dev.storycards.co' | 'images.storycards.co' | 'ix.stories.sc' | 'dev-storycards.imgix.net';
	assets:
		| 'assets-dev.storycards.co'
		| 'assets.storycards.co'
		| 'assts.stories.sc'
		| 'dev-low-cost-galleryassetsbucket9eb44ac2-pkzruw7z30pc.s3.eu-west-1.amazonaws.com';
	videos: 'assts.stories.sc' | 'dev-low-cost-galleryassetsbucket9eb44ac2-pkzruw7z30pc.s3.eu-west-1.amazonaws.com';
	provider: 'CloudFront' | 'Cloudflare';
	updatedAt: Date;
	createdAt: Date;
}

export type StoryType = ValuesType<typeof STORY_TYPE>;

export type StoryModel = {
	id: string;
	templateId?: number;
	clientStoryId: string;
	domainId: null | number;
	teamId: null | string;
	status: 'draft' | 'published';
	createdBy: string;
	organizationId: string;
	name: string;
	type: StoryType | null; // only old stories have null type
	language: SupportedLanguage;
	tags: string[];
	createdAt: string; // ISO
	updatedAt: string; // ISO
	storyVersions: StoryVersions;
	storycardsDomain?: StorycardsDomain;
	storycardsDomainId?: StorycardsDomain['domain'];
	member: {
		id: string;
		name: string;
		profilePictureFile: IProfilePictureFile;
		// get-organization-stories response only
		email?: string;
	};
	team?: {
		id: string;
		name: string;
	};
	opCounter?: number;
};

export type PublishedStoryJSON = Omit<StoryModel, 'storyVersions'> & {
	storyVersion: StoryModel['storyVersions'][string];
};

export type SpreadsheetRange = {
	id: number;
	storySheetIntegrationId: string;
	sheetId: number;
	sheetName: string;
	type: 'single_cell';
	label: string;
	range: string;
	createdAt: string;
	updatedAt: string;
};

export type CardEditorType = {
	data: CardData | null;
	symbols: StorySymbols;
	currentMediaQuery: StoryVersionType['data']['mediaQuery']['defaultPlatform'];
	cardPath: string;
	state: EditableStateInfo;
	storySettings: StorySettingsType;
	storyElements: BBModel[];
	// synchronized by state-sync-middleware.ts at(ISO time)
	stateSyncAt: null | string;
};

export type CardTemplate = {
	id: string;
	organizationId: null | string;
	memberId?: string | null;
	name: string;
	subtitle: string;
	description: string;
	metadata: {
		version: string; // app version when template was created
		cardId: string; // original card id
		storyId: string; // original story id
		screenshot?: Screenshot; // screenshot path for a new templates
		clientStoryId: StoryModel['clientStoryId'];
		defaultPlatform: StoryMediaQuery['defaultPlatform']; // mediaQuery.defaultPlatform of the original story
		storySettings: StoryVersionType['settings'];
	};
	type: 'card';
	language: string;
	categories: string[];
	isPublic?: boolean;
	uiTarget?: StoryType[] | null;
	hoverImage?: number | null;
	hoverImageFile?: IAssetResource | null;
	coverImage?: number | null;
	coverImageFile?: IAssetResource | null;
	createdAt: string;
	updatedAt: string;
	editor: {
		card: {
			_id: CardData['_id'];
			type: CardData['type'];
			name: CardData['name'];
			elements: CardData['elements'];
			settings: CardData['settings'];
			events: CardData['events'];
		};
		symbols?: StoryVersionType['data']['symbols'];
		storyElements?: StoryVersionType['data']['elements'];
	};
};

export type CardTemplateWithoutData = Omit<CardTemplate, 'editor'>;

export type StoryTemplateWithoutData = {
	id: number;
	storyId: string;
	coverCardId?: string;
	title: string;
	description: string | null;
	organizationId: string | null; // null is only for template from scratch
	timesUsed: number;
	language: StoryModel['language'];
	updatedAt: string;
	createdAt: string;
	story: Pick<
		StoryModel,
		| 'id'
		| 'clientStoryId'
		| 'teamId'
		| 'organizationId'
		| 'name'
		| 'language'
		| 'status'
		| 'type'
		| 'tags'
		| 'updatedAt'
		| 'createdAt'
	> & {
		published: Pick<StoryVersionType, 'url' | 'version' | 'settings' | 'updatedAt' | 'createdAt'> & {
			settings: StoryVersionType['settings'] & {
				metadata: { cardTypes: CardData['type'][]; totalCards: number };
			};
		};
	};
};

export type StoryTemplateWithData = StoryTemplateWithoutData & {
	story: StoryTemplateWithoutData['story'] & {
		published: StoryTemplateWithoutData['story']['published'] & { data: StoryVersionType['data'] };
	};
};

export type ComputedComponentPropsFn = (props: {
	element: BBModel; // note: componentProps with state & platforms (not computed one)
	mediaKeysInOrderOfInheritance: StoryMediaPlatform[];
	state: BBStates;
}) => {
	value: BBModel['uiConfig']['componentProps'];
	inheritance: BBComponentPropsInheritance;
};

export type ComputedValueFn = (props: {
	source: string | number | boolean | object | undefined | null;
	sourceKey: keyof BBModel['uiConfig']['componentProps'] | 'textChildren'; // componentProps
	mediaKeysInOrderOfInheritance: StoryMediaPlatform[];
	currState: BBStates;
}) => {
	value: string | number | boolean | object | null;
	inheritance: Partial<Record<string, BBInheritanceValue[] | undefined>> | BBInheritanceValue[] | undefined;
};
